import { useState, SyntheticEvent } from 'react'
import { useRouter } from 'next/router'
import { Button, CountDownTimer, PartialContainer } from '@components'
import { CurrencyInput } from './inflationhedge-potential-input'
import { useAuth } from 'hooks'
import { NextPage } from 'next'
import Link from 'next/link'
import { abbreviateNumber, formatCurrency } from '@utils'
import BackgroundImage from './background-image.png'
import BoxesImage from './boxes.png'

export const InflationHedgePotentialReturn: NextPage<{
  id?: number
  minimumInvestment?: number
  allocation?: number
  closingAt?: any
  setInvestmentAmount: Function
  investmentAmount: number
}> = ({
  id,
  minimumInvestment,
  allocation,
  closingAt,
  setInvestmentAmount,
  investmentAmount,
}) => {
  const router = useRouter()
  const { currentUser } = useAuth()

  const [invAmountError, setInvAmountError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const onInvestmentAmountChange = (value: any) => {
    const _value = parseInt(value || '0')
    setInvestmentAmount(value)

    const min = minimumInvestment
    const max = allocation
    if (min && max && _value >= min && _value <= max) {
      setInvAmountError('')
    }
  }

  const submitInvestmentAmount = (e: SyntheticEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    const min = Number(minimumInvestment)
    const max = Number(allocation)
    const _investmentAmount = Number(investmentAmount)
    if (_investmentAmount < min) {
      setInvAmountError(
        `Must be greater than or equal to ${abbreviateNumber(min)}`,
      )
      setIsSubmitting(false)

      return
    }

    if (_investmentAmount > max) {
      setInvAmountError(
        `Must be less than or equal to ${abbreviateNumber(max)}`,
      )
      setIsSubmitting(false)

      return
    }
    router.push(
      `/invest/company/inflationhedge/deals/${id}/subscribe?amount=${investmentAmount}`,
    )
    setIsSubmitting(false)
  }

  return (
    <div
      id="potential-return"
      className="lg:mt-22 mt-12 h-max bg-cover bg-center bg-no-repeat"
      style={{
        backgroundImage: `url(${BackgroundImage?.src})`,
      }}>
      <PartialContainer>
        <div className="mx-auto flex w-full flex-col items-center pt-24 lg:pt-32">
          <h1 className="text-center font-heading text-2xl font-extrabold text-white lg:text-4xl">
            Calculate your potential returns
          </h1>
          <p className="pb-2 pt-5 text-center text-lg text-white">
            Find out how much you could earn with MarketX Venture’s Structured
            Inflation Hedge product.
          </p>
        </div>
        <div className="mx-auto mt-8 w-full px-5 lg:px-0">
          <form onSubmit={submitInvestmentAmount}>
            <span className="flex flex-col items-center pb-2 text-base text-white opacity-90">
              Enter an amount to invest
            </span>
            <div className="mx-auto w-full lg:w-[27%] 2xl:w-[19%]">
              <CurrencyInput
                error={invAmountError.length > 0}
                value={investmentAmount}
                onChange={onInvestmentAmountChange}
                label=""
                name="investmentAmount"
                inputProps={{
                  min: minimumInvestment,
                  max: allocation,
                }}
              />
              <small className="mt-2 text-xs text-red-500">
                {invAmountError}
              </small>
            </div>
            <div className="mx-auto w-full lg:w-[950px] xl:w-[1000px]">
              <div
                className="mt-5 flex h-max flex-col justify-between rounded-3xl pb-5 lg:mt-8 lg:h-[348px] lg:flex-row lg:rounded-lg lg:bg-cover lg:bg-center lg:bg-no-repeat lg:pb-0"
                style={{
                  backgroundImage: `url(${BoxesImage?.src})`,
                }}>
                {/* US Treasury Bills
                 */}
                <div className="w-full px-6 lg:w-[32%]">
                  <div className="flex items-center justify-center pt-6">
                    <h1 className="mr-2 font-heading text-xl font-semibold text-white">
                      US Treasury Bills
                    </h1>
                    <span className="flex h-[35px] w-[59px] items-center justify-center rounded-[1000px] bg-[#6D45B5] text-white">
                      50%
                    </span>
                  </div>
                  <h1 className="pt-3 text-center font-heading text-2xl text-white">
                    {formatCurrency(investmentAmount / 2)}
                  </h1>
                  <div className="flex flex-col pt-9">
                    <div className="mt-2 flex justify-between border border-b border-l-0 border-r-0 border-t-0 border-[#ffffff1a] pb-2">
                      <h1 className="text-sm text-white">Investment Period</h1>
                      <h1 className="text-sm font-bold text-white">2 years</h1>
                    </div>
                    <div className="mt-2 flex justify-between border border-b border-l-0 border-r-0 border-t-0 border-[#ffffff1a] pb-2">
                      <h1 className="text-sm text-white">Treasury Yield (%)</h1>
                      <h1 className="text-sm font-bold text-white">5%</h1>
                    </div>
                    <div className="mt-2 flex justify-between border border-b border-l-0 border-r-0 border-t-0 border-[#ffffff1a] pb-2">
                      <h1 className="text-sm text-white">
                        Expected Final Value ($)
                      </h1>
                      <h1 className="text-sm font-bold text-white">
                        {formatCurrency(
                          investmentAmount / 2 + (investmentAmount / 2) * 0.1,
                        )}
                      </h1>
                    </div>
                    <div className="mt-2 flex justify-between border border-b border-l-0 border-r-0 border-t-0 border-[#ffffff1a] pb-2">
                      <h1 className="text-sm text-white">
                        Expected Return ($)
                      </h1>
                      <h1 className="text-sm font-bold text-white">
                        {' '}
                        {formatCurrency((investmentAmount / 2) * 0.1)}
                      </h1>
                    </div>
                    <div className="mt-2 flex justify-between">
                      <h1 className="text-sm text-white">
                        Annualized Return (%)
                      </h1>
                      <h1 className="text-sm font-bold text-white">5%</h1>
                    </div>
                  </div>
                </div>
                {/* Pre-IPO Shares
                 */}
                <div className="mt-5 w-full px-6 lg:mt-0 lg:w-[32%]">
                  <div className="flex items-center justify-center pt-6">
                    <h1 className="mr-2 font-heading text-xl font-semibold text-white">
                      Pre-IPO Shares
                    </h1>
                    <span className="flex h-[35px] w-[59px] items-center justify-center rounded-[1000px] bg-[#6D45B5] text-white">
                      50%
                    </span>
                  </div>
                  <h1 className="pt-3 text-center font-heading text-2xl text-white">
                    {formatCurrency(investmentAmount / 2)}
                  </h1>
                  <div className="flex flex-col pt-9">
                    <div className="mt-2 flex justify-between border border-b border-l-0 border-r-0 border-t-0 border-[#ffffff1a] pb-2">
                      <h1 className="text-sm text-white">Investment Period</h1>
                      <h1 className="text-sm font-bold text-white">2 years</h1>
                    </div>
                    <div className="mt-2 flex justify-between border border-b border-l-0 border-r-0 border-t-0 border-[#ffffff1a] pb-2">
                      <h1 className="text-sm text-white">
                        Multiple on Investment
                      </h1>
                      <h1 className="text-sm font-bold text-white">2x</h1>
                    </div>
                    <div className="mt-2 flex justify-between border border-b border-l-0 border-r-0 border-t-0 border-[#ffffff1a] pb-2">
                      <h1 className="text-sm text-white">
                        Expected Final Value ($)
                      </h1>
                      <h1 className="text-sm font-bold text-white">
                        {formatCurrency(
                          investmentAmount / 2 + investmentAmount / 2,
                        )}
                      </h1>
                    </div>
                    <div className="mt-2 flex justify-between border border-b border-l-0 border-r-0 border-t-0 border-[#ffffff1a] pb-2">
                      <h1 className="text-sm text-white">
                        Expected Return ($)
                      </h1>
                      <h1 className="text-sm font-bold text-white">
                        {formatCurrency(investmentAmount / 2)}
                      </h1>
                    </div>
                    <div className="mt-2 flex justify-between">
                      <h1 className="text-sm text-white">
                        Annualized Return (%)
                      </h1>
                      <h1 className="text-sm font-bold text-white">41%</h1>
                    </div>
                  </div>
                </div>
                {/*Total Return
                 */}
                <div className="mt-5 w-full px-6 lg:mt-0 lg:w-[32%]">
                  <div className="flex flex-col items-center pt-6">
                    <h1 className="font-heading text-xl font-semibold text-white">
                      Expected Investment Value
                    </h1>
                    <h1 className="pt-4 text-center font-heading text-2xl text-white">
                      {formatCurrency(
                        investmentAmount / 2 +
                          (investmentAmount / 2) * 0.1 +
                          Number(investmentAmount),
                      )}
                    </h1>
                  </div>

                  <h1 className="pt-2 text-center text-sm text-white">
                    Est. value after 2 years
                  </h1>
                  <div className="flex flex-col pt-3">
                    <div className="mt-2 flex justify-between border border-b border-l-0 border-r-0 border-t-0 border-[#ffffff1a] pb-2">
                      <h1 className="text-sm text-white">Investment Period</h1>
                      <h1 className="text-sm font-bold text-white">2 years</h1>
                    </div>
                    <div className="mt-2 flex justify-between border border-b border-l-0 border-r-0 border-t-0 border-[#ffffff1a] pb-2">
                      <h1 className="text-sm text-white">
                        Multiple on Investment
                      </h1>
                      <h1 className="text-sm font-bold text-white">1.55x</h1>
                    </div>
                    <div className="mt-2 flex justify-between border border-b border-l-0 border-r-0 border-t-0 border-[#ffffff1a] pb-2">
                      <h1 className="text-sm text-white">
                        Expected Final Value ($)
                      </h1>
                      <h1 className="text-sm font-bold text-white">
                        {formatCurrency(
                          investmentAmount / 2 +
                            (investmentAmount / 2) * 0.1 +
                            investmentAmount / 2 +
                            investmentAmount / 2,
                        )}
                      </h1>
                    </div>
                    <div className="mt-2 flex justify-between border border-b border-l-0 border-r-0 border-t-0 border-[#ffffff1a] pb-2">
                      <h1 className="text-sm text-white">
                        Expected Return ($)
                      </h1>
                      <h1 className="text-sm font-bold text-white">
                        {formatCurrency(
                          investmentAmount / 2 + (investmentAmount / 2) * 0.1,
                        )}
                      </h1>
                    </div>
                    <div className="mt-2 flex justify-between">
                      <h1 className="text-sm text-white">
                        Annualized Return (%)
                      </h1>
                      <h1 className="text-sm font-bold text-white">24.50%</h1>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {currentUser ? (
              <div className="pb-22 lg:pb-22 flex flex-col items-center justify-center pt-8 lg:pt-12">
                <p className="pb-2 text-white">
                  Available for a limited time only
                </p>
                <div className="border-1 mx-auto flex w-full flex-col items-center justify-center rounded-md border border-[#6D45B5] px-5 pb-5 lg:w-fit lg:flex-row lg:pb-0">
                  {closingAt && (
                    <div className="w-full text-white lg:mr-12 lg:w-2/5">
                      <CountDownTimer
                        closingAt={new Date(closingAt)}
                        timeOnly
                        backgroundTransparent
                      />
                    </div>
                  )}
                  <div>
                    <Button
                      submit
                      disabled={isSubmitting}
                      loading={isSubmitting}>
                      Invest Now
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="pb-22 lg:pb-22 mt-4 flex justify-center pt-8 lg:pt-12">
                <Link href="/signup">
                  <Button>Request access</Button>
                </Link>
              </div>
            )}
            <div className="mx-auto w-full pb-12 lg:w-[80%]">
              <p className="pt-8 text-sm text-white opacity-80">
                Any performance information shown above is merely an
                illustrative example of a projection. These are not actual
                results. These projections are based upon various assumptions.
                No representation is being made that a performance record
                similar to the projected returns will or is likely to be
                achieved. Please see the Terms of Use, Private Placement
                Memorandum, Operating Agreement, and all other offering
                materials to understand the risk of investment. Any specific
                outcome may or may not be available for any potential
                investment. Further, you select the specific allocation you are
                interested in; thus the performance of those specific investment
                selected will differ.
              </p>
              <p className="pb-26 pt-2 text-sm text-white opacity-80">
                {' '}
                This communication shall not be used or considered as an offer
                to sell or a solicitation of any offer to buy securities of any
                product. Offers can only be made where lawful under, and in
                compliance with, applicable law. MarketX does not provide
                investment advisory, tax, legal or financial services, and
                recommends that you consult a licensed tax, legal, accounting or
                investment professional to perform an independent analysis
                before making any investment decision.
              </p>
            </div>
          </form>
        </div>
      </PartialContainer>
    </div>
  )
}
